import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaPlus, FaTimes, FaLaptop, FaInbox } from "react-icons/fa";
import * as api from "../api/itRequestApi";
import {
  T, CATEGORY_OPTIONS, PRIORITY_OPTIONS, labelFor,
  GlobalStyles, PillSelect, Spinner, StatusPill,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle,
} from "./uiKit";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function NewRequestModal({ onClose, onSubmitted }) {
  const [category, setCategory] = useState("SOFTWARE_INSTALLATION");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const employeeId = localStorage.getItem("employeeId");
      await api.submitRequest(employeeId, {
        category,
        title: title.trim(),
        description: description.trim(),
        priority,
      });
      toast.success("Request submitted");
      onSubmitted();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 460 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            New IT Request
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Category</label>
          <PillSelect value={category} options={CATEGORY_OPTIONS} onChange={setCategory} placeholder="Select category" />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Title</label>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Install IntelliJ IDEA"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add details — what you need and why"
            rows={4}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>Priority</label>
          <PillSelect value={priority} options={PRIORITY_OPTIONS} onChange={setPriority} placeholder="Select priority" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
          <button
            type="button"
            disabled={saving || !title.trim()}
            onClick={handleSubmit}
            style={{ ...primaryBtnStyle, opacity: saving || !title.trim() ? 0.55 : 1, cursor: saving || !title.trim() ? "not-allowed" : "pointer" }}
          >
            {saving ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: T.ink2, marginBottom: 6, fontFamily: T.fontBody,
};

export default function ITRequestPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const employeeId = localStorage.getItem("employeeId");
      const data = await api.listByEmployee(employeeId);
      setRequests(data || []);
    } catch (e) {
      toast.error(e.message || "Failed to load your requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  return (
    <div style={{ flex: 1, minHeight: 0, background: T.bg, fontFamily: T.fontBody, overflowY: "auto" }} className="itreq-scroll">
      <GlobalStyles />

      <div style={{ padding: "26px 34px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: T.primary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaLaptop size={15} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>IT Support</h2>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Submit a request and track its status</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
              borderRadius: 9, border: "none", background: T.primary, color: "#fff",
              fontWeight: 600, fontSize: 13.5, cursor: "pointer", fontFamily: T.fontBody,
              boxShadow: "0 1px 2px rgba(99,102,241,0.3)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = T.primaryDark)}
            onMouseLeave={(e) => (e.currentTarget.style.background = T.primary)}
          >
            <FaPlus size={11} /> New Request
          </button>
        </div>

        {loading ? (
          <Spinner />
        ) : requests.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            color: T.ink3, marginTop: 56, textAlign: "center",
          }}>
            <FaInbox size={26} style={{ color: T.borderStrong }} />
            <div style={{ fontSize: 14, color: T.ink2, fontWeight: 600, fontFamily: T.fontDisplay }}>No requests yet</div>
            <div style={{ fontSize: 13, maxWidth: 280 }}>Submit your first IT request — software installs, hardware issues, access requests, and more.</div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, boxShadow: T.shadow, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <div className="itreq-scroll" style={{ overflow: "auto", maxHeight: "calc(100vh - 260px)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfc", borderBottom: `1px solid ${T.border}` }}>
                    {["S.NO", "Category", "Title", "Description", "Priority", "Status", "Submitted", "Admin Remarks"].map((h) => (
                      <th key={h} style={theadCellStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r, idx) => (
                    <tr key={r.id} className="itreq-row" style={{ borderBottom: idx === requests.length - 1 ? "none" : `1px solid ${T.border}` }}>
                      <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{idx + 1}</td>
                      <td style={tdCellStyle}>{labelFor(CATEGORY_OPTIONS, r.category)}</td>
                      <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1 }}>{r.title}</td>
                      <td style={{ ...tdCellStyle, ...descCellStyle }} title={r.description || ""}>{r.description || "—"}</td>
                      <td style={tdCellStyle}>{labelFor(PRIORITY_OPTIONS, r.priority)}</td>
                      <td style={tdCellStyle}><StatusPill status={r.status} /></td>
                      <td style={{ ...tdCellStyle, fontFamily: T.fontMono, fontSize: 12 }}>{formatDateTime(r.createdAt)}</td>
                      <td style={{ ...tdCellStyle, color: T.ink3 }}>{r.adminRemarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <NewRequestModal onClose={() => setShowModal(false)} onSubmitted={loadRequests} />
      )}
    </div>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1,
};
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13.5, color: T.ink2, verticalAlign: "top",
};
const descCellStyle = {
  maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default",
};
